import type { AdminWorkspace, PublicWorkspace } from '../types'
import { STANDARD_CASE_STUDY_ASSETS, STANDARD_PRODUCT_ASSETS } from './standard-content'

export const NEXT_DEMO_PUBLIC_ID = 'next-demo-preview'

const publicWorkspace: PublicWorkspace = {
  id: '3b2118a4-2020-4fc2-8e5d-nextdemopreview',
  publicId: NEXT_DEMO_PUBLIC_ID,
  accountName: 'Pinnacle Career Institute',
  accountLogoUrl: null,
  customerSegment: 'all',
  phase: 'sales',
  pricingModel: null,
  pricingModes: 'auto',
  firstCallRecorded: false,
  overview: 'One career-services experience for resume support, job discovery, employer connections, community, and faster graduate employment.',
  recap: null,
  currentNextStep: 'Use Friday’s conversation to walk through the student career portal and confirm how it could fit into Pinnacle’s final class.',
  precallOnepager: {
    problem: 'Pinnacle wants to give students more Career Services support without making every interaction depend on staff availability. Matt is looking for one portal where students can improve resumes with AI, find jobs, learn job-search best practices, connect with employers, and feel part of a career community.',
    costOfInaction: 'Without a consistent career workflow, students reach graduation with uneven preparation, staff repeat the same guidance, and employer opportunities are harder to turn into faster placements.',
    steps: [
      {
        title: 'Prepare students before graduation',
        detail: 'Use AI resume support, job-search guidance, and assignments in the final class to build confidence while students are still engaged.',
      },
      {
        title: 'Create one career community',
        detail: 'Bring job opportunities, employer profiles, events, peer support, and career resources into one portal students return to.',
      },
      {
        title: 'Help graduates move into work faster',
        detail: 'Give the Career Services team visibility into each job search while Prentus tracks who gets hired, where, and what they earn.',
      },
    ],
    proofPoints: [
      'QuickStart reached more than 60% career-services engagement while reducing refund requests by 17%.',
      'Fullstack Academy supported 3,800 students with 3× greater operational efficiency.',
    ],
    nextStep: 'Use Friday’s conversation to walk through the student portal, AI resume support, job discovery, employer experience, community, and options for incorporating Prentus into the final class.',
    enabled: true,
    audienceName: 'Matt Beer',
    audienceTitle: 'Campus President and Senior Vice President',
    roi: {
      annualValue: 369_221,
      tuitionRetained: 290_484,
      studentsRetained: 12.38,
      enrollment: 619,
      retentionRatePercent: 69.33,
      completionRatePercent: 60,
      revenuePerStudent: 23_464,
      retentionLiftPercent: 2,
      assumptions: 'Estimate uses a 2-point retention lift, the latest federal enrollment and retention figures, and Pinnacle’s published $23,464 tuition for its 40-week certificate programs. It is a planning model, not a guarantee.',
      sources: [
        { label: 'U.S. Department of Education College Scorecard', url: 'https://collegescorecard.ed.gov/school/?177302-Pinnacle-Career-Institute' },
        { label: 'Pinnacle 2026-27 catalog', url: 'https://pinnacleapps.com/pcitrainingweb/pci_catalog.pdf' },
      ],
    },
  },
  successContent: null,
  projects: [],
  renewal: null,
  orderForms: [],
  prequalificationOnly: true,
  businessCase: [],
  members: [],
  tasks: [],
  assets: [...STANDARD_PRODUCT_ASSETS, ...STANDARD_CASE_STUDY_ASSETS],
  calculatorInputs: {},
  viewerEmail: 'preview@pcitraining.edu',
  updatedAt: '2026-08-20T16:00:00.000Z',
}

export const NEXT_DEMO_PUBLIC_WORKSPACE = publicWorkspace

export const NEXT_DEMO_ADMIN_WORKSPACE: AdminWorkspace = {
  ...publicWorkspace,
  status: 'published',
  companyHubspotId: 'pinnacle-next-demo-preview',
  deals: [],
  allowedDomains: ['pcitraining.edu'],
  pricingVisibility: 'hidden',
  pendingApprovals: 0,
  suggestions: [],
  sandboxRequests: [],
  qualification: {
    id: 'codex-qualification:pinnacle-demo-call',
    reason: 'The completed first conversation confirmed a concrete Career Services use case and a follow-up product review.',
    dealName: 'Pinnacle Career Institute - New Partnership',
    dealStage: 'tech_demo',
    sourceCallId: 'fathom:pinnacle-demo-call',
    sourceCallTitle: 'Pinnacle Career Institute and Prentus',
    occurredAt: '2026-08-20T17:00:00.000Z',
    contacts: [{ id: '10001', email: 'matt@pcitraining.edu', name: 'Matt Beer', title: 'Campus President and Senior Vice President', selected: true }],
    email: {
      subject: 'Pinnacle Career Institute <> Prentus Follow Up',
      body: 'Hey Matthew,\n\nReally enjoyed our conversation today. You said the retention story matters most, that students who see a path to a job early stick around, and that is exactly where we can help.\n\nA few things I heard:\n\n- Your students can see a clear path to the job from their first week instead of their last term.\n- Your employer network can grow through Talent Links without employers signing up for anything.\n- Quinlynn and her team can scale support with AI advising and automated outcome tracking.\n\nYou can review the product, case studies, and pricing calculator in this workspace: https://partner.prentus.com/next-demo-preview\n\nNext Steps\nWe will review the student and admin experience together on September 11 at 2:00 PM ET. If you need anything before then, just let me know.',
      requiredContent: {
        warmOpening: 'Really enjoyed our conversation today.',
        primaryValue: 'You said the retention story matters most, that students who see a path to a job early stick around, and that is exactly where we can help.',
        primaryConcern: null,
        promisedResources: ['workspace'],
        confirmedNextStep: 'We will review the student and admin experience together on September 11 at 2:00 PM ET. If you need anything before then, just let me know.',
        nextStepDate: '2026-09-11',
        nextStepParticipants: ['Matt Beer', 'Rod Danan'],
      },
      thread: { threadId: null, messageId: null },
    },
  },
  followUp: null,
  reviewWarnings: [],
  timeline: [],
}
